import { post, getAIBaseURL } from '../actions/ajax';

/**
 * AI Native Behavior Evaluator
 *
 * Provides an abstraction layer for calling AI service endpoints from the web application.
 * This ensures consistent error handling and provides a single point of change if we need
 * to modify how AI service calls are made in the future.
 *
 * Note: We call the AI service directly (not through the server) because:
 * - Rate limiting is enforced at the AI service level
 * - Authentication tokens are already available in the web app
 * - The server route would just be a pass-through with no added value
 * - Reduces latency by eliminating an unnecessary network hop
 *
 * Similar to serverNativeBehaviorEvaluator, but specifically for AI service endpoints.
 */
const evaluate = async (endpoint, data) => {
  const baseURL = getAIBaseURL();
  // Ensure exactly one slash between baseURL and endpoint
  // Remove trailing slash from baseURL and leading slash from endpoint, then join with /
  const normalizedBaseURL = baseURL.replace(/\/+$/, '');
  const normalizedEndpoint = endpoint.replace(/^\/+/, '');
  const url = "".concat(normalizedBaseURL, "/").concat(normalizedEndpoint);
  try {
    // Call AI service directly - rate limiting is handled at the AI service level
    const response = await post(url, data, false);
    return response.data;
  } catch (error) {
    var _error$response, _error$response2;
    // Re-throw with more context
    const errorMessage = ((_error$response = error.response) === null || _error$response === void 0 || (_error$response = _error$response.data) === null || _error$response === void 0 ? void 0 : _error$response.message) || error.message || 'AI service request failed';
    const errorStatus = ((_error$response2 = error.response) === null || _error$response2 === void 0 ? void 0 : _error$response2.status) || 500;
    const enhancedError = new Error(errorMessage);
    enhancedError.status = errorStatus;
    enhancedError.response = error.response;
    throw enhancedError;
  }
};

/**
 * Call AI filters endpoint directly
 * @param {string} prompt - The search prompt
 * @param {string} cabinetIdentifier - Cabinet identifier
 * @returns {Promise<Object>} Filters object (extracted from response.filters)
 * @note Authentication is handled automatically via JWT token. The customer identifier
 *   is extracted from the JWT token by the AI service, not from the request body.
 */
const filters = async (prompt, cabinetIdentifier) => {
  // Call AI service directly - rate limiting enforced at AI service level
  // Authentication is handled automatically via JWT token
  const response = await evaluate('agents/filters', {
    prompt,
    cabinetIdentifier
  });
  // AI service returns { filters: {...} }, extract the filters object
  return response.filters || {};
};

/**
 * Call qualityAgent endpoint directly
 * @param {Array} checks - Array of checks to evaluate
 * @returns {Promise<Object>} Quality agent response with report
 * @note Authentication is handled automatically via JWT token. The customer identifier
 *   is extracted from the JWT token by the AI service, not from the request body.
 */
const qualityAgent = async checks => evaluate('agents/qualityAgent', {
  checks
});

/**
 * Call qualityAgent followup investigation endpoint directly
 * @param {Object} suggestion - Followup suggestion object
 * @param {Object} originalContext - Original context object
 * @returns {Promise<Object>} Followup investigation response
 * @note Authentication is handled automatically via JWT token. The customer identifier
 *   is extracted from the JWT token by the AI service, not from the request body.
 */
const qualityAgentFollowUp = async (suggestion, originalContext) => evaluate('agents/qualityAgent/followup', {
  suggestion,
  originalContext
});

/**
 * Call qualityAgent generate checks endpoint directly
 * @param {Array} existingChecks - Existing checks array
 * @param {string} userRequest - User's request text
 * @param {Array} ignoredChecks - Array of ignored check IDs
 * @returns {Promise<Object>} Generated checks response
 * @note Authentication is handled automatically via JWT token. The customer identifier
 *   is extracted from the JWT token by the AI service, not from the request body.
 */
const qualityAgentGenerateChecks = async (existingChecks, userRequest, ignoredChecks) => evaluate('agents/qualityAgent/generateChecks', {
  existingChecks,
  userRequest,
  ignoredChecks
});

/**
 * Call LLM generate endpoint (used by aiPromptBehavior and llmBehavior).
 * @param {string} prompt - The prompt text to send to the LLM
 * @returns {Promise<string|null>} Response text, or null when response is not a string.
 * @throws {Error} When the AI service returns an error (coerced to string if needed).
 * @note AI returns { response, model, provider } or { error, model, provider }.
 */
const llmGenerate = async prompt => {
  const result = await evaluate('llm/generate', {
    prompt
  });
  if ((result === null || result === void 0 ? void 0 : result.error) != null) {
    const errMsg = typeof result.error === 'string' ? result.error : String(result.error);
    const err = new Error(errMsg);
    err.response = {
      data: {
        error: errMsg,
        message: errMsg
      }
    };
    throw err;
  }
  return typeof (result === null || result === void 0 ? void 0 : result.response) === 'string' ? result.response : null;
};

/**
 * Call agents/parse endpoint directly (used by aiDocumentParseBehavior).
 * @param {string} customer - Customer identifier
 * @param {string|string[]} documentKeys - Document key(s) to parse
 * @param {Object} schema - Schema for extraction
 * @param {string} prompt - Extraction prompt
 * @returns {Promise<Array|Object>} Parse result (array or object per AI service)
 */
const documentParse = async (customer, documentKeys, schema, prompt) => {
  const result = await evaluate('agents/parse', {
    customer,
    documentKeys,
    schema,
    prompt
  });
  return result;
};
const aiNativeBehaviorEvaluator = {
  documentParse,
  evaluate,
  filters,
  llmGenerate,
  qualityAgent,
  qualityAgentFollowUp,
  qualityAgentGenerateChecks
};
export default aiNativeBehaviorEvaluator;